/**
 * Rising Auctions cart expiry cleanup.
 * Keeps RA-specific validation/removal outside the generic cart components.
 */
(function () {
  'use strict';

  var RA = window.RA || {};
  window.RA = RA;

  var CLEANUP_SOURCE = 'rising-auctions-cart-cleanup';
  var cleanupPromise = null;

  function getCartUpdateUrl() {
    var routes = window.Theme && window.Theme.routes ? window.Theme.routes : {};
    var url = routes.cart_update_url || '/cart/update';
    return url.endsWith('.js') ? url : url + '.js';
  }

  function getCustomerId() {
    var bodyCustomerId = document.body && document.body.dataset ? document.body.dataset.raCustomerId : '';
    if (bodyCustomerId) return bodyCustomerId;

    var sectionWithCustomer = document.querySelector('[data-customer-id]');
    var sectionCustomerId = sectionWithCustomer instanceof HTMLElement ? sectionWithCustomer.dataset.customerId : '';
    if (sectionCustomerId) return sectionCustomerId;

    var customerInput = document.getElementById('customerNumber');
    return customerInput instanceof HTMLInputElement ? customerInput.value.trim() : '';
  }

  function isRisingAuctionLine(item) {
    var properties = item && item.properties ? item.properties : {};
    return properties._itemSource === 'RISING' || properties.itemSource === 'RISING';
  }

  function getAuctionCode(item) {
    var properties = item && item.properties ? item.properties : {};
    var auctionCode = properties._raAuctionCode || properties['Auction code'] || properties.auctionCode || '';
    return auctionCode == null ? '' : String(auctionCode).trim();
  }

  function getPaymentExpirationAt(item) {
    var properties = item && item.properties ? item.properties : {};
    return properties._paymentExpirationAt || properties.paymentExpirationAt || '';
  }

  /**
   * "Now" for expiry checks. This decides whether a paid-for-later auction line is dropped from
   * the shopper's cart, so it must run on the RA server clock: a device clock running fast would
   * silently remove lines whose payment window is still open. Falls back to Date.now() when
   * rising-auctions-server-clock.js is absent.
   */
  function raNow() {
    var clock = window['RAServerClock'];
    return clock ? clock.now() : Date.now();
  }

  /** Feed the shared clock from any RA response carrying serverTime. */
  function raSyncClock(data, sentAt) {
    var clock = window['RAServerClock'];
    if (clock) clock.syncFromPayload(data, sentAt);
  }

  /** Resolves once the clock has an offset (or immediately if it already has one, or is absent). */
  function raEnsureClock() {
    var clock = window['RAServerClock'];
    if (!clock) return Promise.resolve();
    return Promise.resolve(clock.ensureSynced());
  }

  function isExpiredIsoDate(value) {
    if (value == null || value === '') return false;
    var timestamp = new Date(String(value)).getTime();
    return Number.isFinite(timestamp) && timestamp <= raNow();
  }

  function getCartItemsSectionIds() {
    var sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach(function (component) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    return Array.from(sectionIds);
  }

  function updateCartCountIndicators(count) {
    var normalizedCount = Number.isFinite(count) ? count : 0;
    document.querySelectorAll('.cart-bubble__text-count').forEach(function (el) {
      el.textContent = normalizedCount < 100 ? String(normalizedCount) : '';
      el.classList.toggle('hidden', normalizedCount === 0);
    });
    document.querySelectorAll('.cart-bubble').forEach(function (bubble) {
      bubble.classList.toggle('visually-hidden', normalizedCount === 0);
      bubble.classList.remove('hidden');
    });
    document.querySelectorAll('cart-icon').forEach(function (icon) {
      icon.classList.toggle('header-actions__cart-icon--has-cart', normalizedCount > 0);
    });
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(normalizedCount), timestamp: Date.now() }));
    } catch (e) {
      // no-op
    }
  }

  function dispatchCartUpdate(cart, sourceId) {
    var sections = (cart && cart.sections) || {};
    var itemCount = Number((cart && cart.item_count) || 0);
    updateCartCountIndicators(itemCount);
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart || {},
        sourceId: sourceId || getCartItemsSectionIds()[0] || CLEANUP_SOURCE,
        data: {
          action: 'remove',
          itemCount: itemCount,
          source: CLEANUP_SOURCE,
          sections: sections
        }
      }
    }));
  }

  function validateAuctionCodes(auctionCodes) {
    var customerId = getCustomerId();
    if (!customerId || auctionCodes.length === 0) return Promise.resolve(new Set());

    var params = new URLSearchParams();
    params.set('auction_ids', auctionCodes.join(','));
    params.set('logged_in_customer_id', customerId);

    var validationSentAt = Date.now();
    return fetch('/apps/rising-auctions/cart-validation?' + params.toString(), {
      credentials: 'include'
    })
      .then(function (response) { return response.json(); })
      .then(function (payload) {
        raSyncClock(payload, validationSentAt);
        var invalidAuctionIds = Array.isArray(payload && payload.invalidAuctionIds) ? payload.invalidAuctionIds : [];
        return new Set(invalidAuctionIds.map(function (id) { return String(id); }));
      });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'include' })
      .then(function (response) { return response.json(); })
      .catch(function () { return null; });
  }

  RA.cleanupExpiredAuctionCartLines = function (options) {
    options = options || {};
    if (cleanupPromise) return cleanupPromise;

    // Sync the clock alongside the cart fetch (not after it): the local expiry check below runs
    // before cart-validation is called, so it needs the offset already in place.
    cleanupPromise = Promise.all([
      Promise.resolve(options.cart || fetchCart()),
      raEnsureClock()
    ])
      .then(function (resolved) {
        var cart = resolved[0];
        var items = Array.isArray(cart && cart.items) ? cart.items : [];
        var risingAuctionItems = items.filter(isRisingAuctionLine);
        if (risingAuctionItems.length === 0) {
          return { removedCount: 0, cart: cart };
        }

        var locallyExpiredKeys = new Set();
        var auctionCodes = new Set();
        risingAuctionItems.forEach(function (item) {
          var expirationAt = getPaymentExpirationAt(item);
          if (item && item.key && isExpiredIsoDate(expirationAt)) {
            locallyExpiredKeys.add(item.key);
          }
          var auctionCode = getAuctionCode(item);
          if (auctionCode) auctionCodes.add(auctionCode);
        });

        return validateAuctionCodes(Array.from(auctionCodes))
          .catch(function (error) {
            console.error('[RA] Failed to validate auction cart lines', error);
            return new Set();
          })
          .then(function (invalidAuctionCodes) {
            var keysToRemove = new Set(locallyExpiredKeys);
            risingAuctionItems.forEach(function (item) {
              var auctionCode = getAuctionCode(item);
              if (item && item.key && auctionCode && invalidAuctionCodes.has(auctionCode)) {
                keysToRemove.add(item.key);
              }
            });

            if (keysToRemove.size === 0) {
              return { removedCount: 0, cart: cart };
            }

            var sectionIds = getCartItemsSectionIds();
            var updates = {};
            Array.from(keysToRemove).forEach(function (key) {
              updates[key] = 0;
            });

            var requestPayload = { updates: updates };
            if (sectionIds.length > 0) {
              requestPayload.sections = sectionIds.join(',');
              requestPayload.sections_url = window.location.pathname;
            }

            return fetch(getCartUpdateUrl(), {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestPayload)
            })
              .then(function (response) { return response.json(); })
              .then(function (updatedCart) {
                if (options.refreshSections !== false) {
                  dispatchCartUpdate(updatedCart, options.sourceId);
                }
                return { removedCount: keysToRemove.size, cart: updatedCart };
              });
          });
      })
      .finally(function () {
        cleanupPromise = null;
      });

    return cleanupPromise;
  };

  function isCheckoutSubmitter(element) {
    if (!(element instanceof Element)) return null;
    return element.closest('button[name="checkout"], input[name="checkout"]');
  }

  function runCheckoutCleanupGuard(submitter, form) {
    return RA.cleanupExpiredAuctionCartLines().then(function (result) {
      var itemCount = Number(result && result.cart ? result.cart.item_count : 0);
      if (itemCount <= 0) return;

      var currentSubmitter =
        submitter instanceof HTMLElement && submitter.isConnected
          ? submitter
          : document.querySelector(
              'cart-drawer-component button[name="checkout"], cart-drawer-component input[name="checkout"], button[name="checkout"], input[name="checkout"]'
            );

      if (currentSubmitter instanceof HTMLElement) {
        currentSubmitter.dataset.raCheckoutCleanupDone = 'true';
        currentSubmitter.click();
        window.setTimeout(function () {
          delete currentSubmitter.dataset.raCheckoutCleanupDone;
        }, 0);
        return;
      }

      if (form instanceof HTMLFormElement) {
        form.dataset.raCheckoutCleanupDone = 'true';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
        window.setTimeout(function () {
          delete form.dataset.raCheckoutCleanupDone;
        }, 0);
      }
    });
  }

  function initCheckoutGuard() {
    document.addEventListener('click', function (event) {
      var submitter = isCheckoutSubmitter(event.target);
      if (!(submitter instanceof HTMLElement)) return;
      if (submitter.dataset.raCheckoutCleanupDone === 'true') return;
      if (submitter.hasAttribute('disabled')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      runCheckoutCleanupGuard(
        submitter,
        submitter.getAttribute('form') ? document.getElementById(submitter.getAttribute('form')) : submitter.closest('form')
      ).catch(function (error) {
        console.error('[RA] Checkout cart cleanup failed', error);
      });
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.raCheckoutCleanupDone === 'true') return;

      var submitter =
        event.submitter instanceof HTMLElement && event.submitter.matches('button[name="checkout"], input[name="checkout"]')
          ? event.submitter
          : form.querySelector('button[name="checkout"], input[name="checkout"]');
      if (!submitter) return;
      if (submitter instanceof HTMLElement && submitter.dataset.raCheckoutCleanupDone === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      runCheckoutCleanupGuard(submitter, form).catch(function (error) {
        console.error('[RA] Checkout cart cleanup failed', error);
      });
    }, true);
  }

  initCheckoutGuard();

  window.addEventListener('pageshow', function () {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(function (btn) {
      delete btn.dataset.raCheckoutCleanupDone;
    });
  });
})();
